import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { TranslateModule } from '@ngx-translate/core';

import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';

import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getLocale } from '@vendasta/galaxy/utility/locale';

import { map, take } from 'rxjs/operators';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AssociateUserToOrderComponent } from '../associate-user-to-order/associate-user-to-order.component';
import {
  AssociatedUserIDType,
  AssociateUserToOrderService,
} from '../associate-user-to-order/associate-user-to-order.service';
import { Observable } from 'rxjs';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { ConnectedPosition } from '@angular/cdk/overlay';

export interface Customer {
  userId?: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface SubmitCustomerDialogComponentData {
  partnerId: string;
  accountGroupId: string;
  currentExpiry?: Date;
  warningMessage?: string;
  canCollectPaymentFromCustomer: boolean;
  openOrderPreviewDialog: (shouldCollectPayment: boolean) => void;
  earliestScheduledLineItemDate?: Date | null;
  hasAlreadyActiveProducts?: boolean;
}

export interface SubmitCustomerDialogComponentResult {
  user: Customer;
  expiry?: Date;
  shouldCollectPayment?: boolean;
}

@Component({
  templateUrl: './submit-customer-dialog.component.html',
  styleUrls: ['./submit-customer-dialog.component.scss'],
  imports: [
    CommonModule,
    GalaxyAlertModule,
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    GalaxyPipesModule,
    GalaxyBadgeModule,
    MatInputModule,
    MatTooltipModule,
    MatCheckboxModule,
    GalaxyFormFieldModule,
    AssociateUserToOrderComponent,
    GalaxyPopoverModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: getLocale() },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
  ],
})
export class SubmitCustomerDialogComponent {
  readonly accountGroupId: string;
  readonly partnerId: string;
  readonly warningMessage: string;
  // minDate is tomorrow since we don't allow setting offer expiry for today
  readonly minDate: Date = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  })();
  readonly canCollectPaymentFromCustomer: boolean;
  readonly maxDate: Date | null = null;

  readonly expiryDateControl = new FormControl<Date | null>(null, [Validators.required, datetimeValidator]);

  shouldCollectPayment = false;
  hasAlreadyActiveProducts = false;
  showPopover = false;
  position?: ConnectedPosition;

  constructor(
    public dialogRef: MatDialogRef<SubmitCustomerDialogComponent, SubmitCustomerDialogComponentResult>,
    @Inject(MAT_DIALOG_DATA) public data: SubmitCustomerDialogComponentData,
    private readonly snackbarService: SnackbarService,
    readonly associateUserToOrderService: AssociateUserToOrderService,
  ) {
    this.partnerId = data.partnerId;
    this.accountGroupId = data.accountGroupId;
    this.warningMessage = data.warningMessage;
    this.canCollectPaymentFromCustomer = data.canCollectPaymentFromCustomer;
    this.hasAlreadyActiveProducts = data.hasAlreadyActiveProducts;

    if (this.data.currentExpiry) {
      this.expiryDateControl.setValue(this.data.currentExpiry);
      this.expiryDateControl.markAsUntouched();
    }

    if (this.data.earliestScheduledLineItemDate) {
      this.maxDate = this.data.earliestScheduledLineItemDate;
    }
  }

  protected success() {
    this.getResult().subscribe({
      next: (customer) => {
        this.dialogRef.close(customer);
      },
      error: (_) => {
        this.snackbarService.openErrorSnack('SALES_ORDERS.SUBMIT_ORDER_FOR_APPROVAL.ORDER_SUBMISSION_FAILED');
      },
    });
  }

  getResult(): Observable<SubmitCustomerDialogComponentResult> {
    return this.associateUserToOrderService.createVBCUserFromCustomer$(AssociatedUserIDType.subjectID).pipe(
      map((user) => {
        const res: SubmitCustomerDialogComponentResult = {
          user: user,
        };

        const expiryDate = this.expiryDateControl.value;
        if (expiryDate) {
          expiryDate.setUTCHours(0, 0, 0, 0);
          res.expiry = expiryDate;
        }

        if (this.canCollectPaymentFromCustomer) {
          res.shouldCollectPayment = this.shouldCollectPayment;
        }
        return res;
      }),
      take(1),
    );
  }

  get invalid(): boolean {
    return this.expiryDateControl.invalid;
  }

  protected openOrderPreviewDialog() {
    this.data.openOrderPreviewDialog(this.shouldCollectPayment);
  }

  showPopovers(hasAlreadyActiveProducts: boolean, position: ConnectedPosition): void {
    if (hasAlreadyActiveProducts) {
      this.position = position;
      this.showPopover = true;
    }
  }

  hidePopovers(): void {
    this.showPopover = false;
  }

  protected readonly PopoverPositions = PopoverPositions;
}

export function datetimeValidator(c: FormControl<Date | null>): { [error: string]: any } | null {
  const expiryDate = c.value;
  if (!expiryDate) {
    return null;
  }

  const today = new Date();
  if (expiryDate > today) {
    return null;
  } else {
    return { dateValidator: { valid: false } };
  }
}
